interface IClientInfo {
  ipAddress: string;
  country: string;
  city: string;
  userAgent: string;
  timezone: string;
  utc_offset: string;
  language: string;
}

interface IClientInfoResponse {
  ip?: string;
  origin?: string;
  country_name?: string;
  country?: string;
  city?: string;
  timezone?: string;
  utc_offset?: string;
  [key: string]: any;
}

class ClientInfoBuilder {
  private clientInfo: IClientInfo;

  constructor() {
    this.clientInfo = {
      ipAddress: 'unknown',
      country: 'unknown',
      city: 'unknown',
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: 'unknown',
      language: navigator.language,
    };
  }

  setIp(value: string): ClientInfoBuilder {
    if (value) this.clientInfo.ipAddress = value;
    return this;
  }

  setCountry(value: string): ClientInfoBuilder {
    if (value) this.clientInfo.country = value;
    return this;
  }

  setCity(value: string): ClientInfoBuilder {
    if (value) this.clientInfo.city = value;
    return this;
  }

  setUtcOffset(value: string): ClientInfoBuilder {
    if (value) this.clientInfo.utc_offset = value;
    return this;
  }

  fromApiResponse(data: IClientInfoResponse): ClientInfoBuilder {
    return this.setIp(data.ip || data.origin || '')
      .setCountry(data.country_name || data.country || '')
      .setCity(data.city || '')
      .setUtcOffset(data.utc_offset || '');
  }

  build(): IClientInfo {
    return { ...this.clientInfo };
  }
}

const CLIENT_INFO_URLS = {
  ipapi: 'https://ipapi.co/json/',
};

const commonPromise = async (url: string, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { signal: controller.signal });
    return await res.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

const isCypressTestRun = () =>
  typeof window !== 'undefined' &&
  typeof (window as Window & { Cypress?: unknown }).Cypress !== 'undefined';

const getClientInfo = async (): Promise<IClientInfo> => {
  const builder = new ClientInfoBuilder();

  if (isCypressTestRun()) {
    return builder.build();
  }

  try {
    const data: IClientInfoResponse = await commonPromise(CLIENT_INFO_URLS.ipapi);
    builder.fromApiResponse(data);
  } catch (error) {
    console.warn('Could not fetch client info:', error);
  }

  return builder.build();
};

export default getClientInfo;
export type { IClientInfo };
